/**
 * Composant de synthèse globale des statistiques multi-devises
 * Affiche un aperçu consolidé de toutes les devises confondues
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Paper,
  Avatar,
  Chip,
  Divider,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Public as GlobalIcon,
  AccountBalance as ReservationIcon,
  SwapHoriz as TransactionIcon,
  LocalAtm as DepositIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as StatsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as PendingIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  Flag as FlagIcon
} from '@mui/icons-material';
import { formatDate, formatDateUniversal } from '../../../utils/formatters';
import { formatGlobalAmount, calculatePercentage } from '../../../utils/currencyStatsAggregator';

const OwnerGlobalCurrencyStatsView = ({ 
  globalStats, 
  loading, 
  error,
  onRefresh 
}) => {
  const { t } = useTranslation('financial');
  
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Avatar sx={{ bgcolor: '#9c27b0' }}>
              <GlobalIcon />
            </Avatar>
            <Typography variant="h6">
              {t('globalStats.loading', 'Chargement de la synthèse globale...')}
            </Typography>
          </Box>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Avatar sx={{ bgcolor: 'error.main' }}>
              <GlobalIcon />
            </Avatar>
            <Typography variant="h6" color="error">
              {t('globalStats.error', 'Erreur de chargement')}
            </Typography>
          </Box>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!globalStats) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Avatar sx={{ bgcolor: 'grey.400' }}>
              <GlobalIcon />
            </Avatar>
            <Typography variant="h6">
              {t('globalStats.noData', 'Aucune donnée globale disponible')}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const { 
    global_reservations, 
    global_transactions, 
    global_deposits,
    global_activity,
    currencies_included,
    currencies_with_errors,
    metadata
  } = globalStats;

  // Fonction pour traduire les libellés de statut
  const translateStatus = (status, type = 'payment') => {
    if (!status) return '';
    const key = type === 'reservation'
      ? `reservationStatuses.${status}`
      : `payment_status.${status}`;
    const result = t(key);
    // If not found, i18next returns the key path (contains '.') — fall back to raw
    return result.includes('.') ? status : result;
  };

  // Fonction pour obtenir l'icône du statut
  const getStatusIcon = (status) => {
    const iconProps = { fontSize: 'small' };
    
    switch (status?.toLowerCase()) {
      case 'confirmed':
      case 'completed':
      case 'paid':
        return <CheckCircleIcon color="success" {...iconProps} />;
      case 'pending':
      case 'processing':
        return <PendingIcon color="warning" {...iconProps} />;
      case 'cancelled':
      case 'failed':
      case 'refunded':
        return <CancelIcon color="error" {...iconProps} />;
      default:
        return <PendingIcon color="info" {...iconProps} />;
    }
  };

  // Fonction pour obtenir la couleur du statut
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
      case 'completed':
      case 'paid':
        return 'success';
      case 'pending':
      case 'processing':
        return 'warning';
      case 'cancelled':
      case 'failed':
      case 'refunded':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Card elevation={3}>
      <CardContent>
        {/* En-tête global */}
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Avatar sx={{ bgcolor: 'linear-gradient(45deg, #9c27b0 30%, #e1bee7 90%)', width: 48, height: 48 }}>
            <GlobalIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {t('globalStats.title', '🌍 SYNTHÈSE GLOBALE')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('globalStats.consolidation', { count: currencies_included.length, defaultValue: `Consolidation de ${currencies_included.length} devise(s)` })}
            </Typography>
          </Box>
        </Box>

        {/* Métriques principales */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={3}>
            <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: '#1976d2', mx: 'auto', mb: 1 }}>
                <ReservationIcon />
              </Avatar>
              <Typography variant="h4" fontWeight="bold" color="primary">
                {global_reservations.total_count}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('globalStats.totalReservations', 'Réservations Totales')}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={3}>
            <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: '#388e3c', mx: 'auto', mb: 1 }}>
                <TransactionIcon />
              </Avatar>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {global_transactions.total_count}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('globalStats.totalTransactions', 'Transactions Totales')}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={3}>
            <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: '#f57c00', mx: 'auto', mb: 1 }}>
                <DepositIcon />
              </Avatar>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {global_deposits.total_count}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('globalStats.totalDeposits', 'Dépôts Totaux')}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={3}>
            <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: '#9c27b0', mx: 'auto', mb: 1 }}>
                <FlagIcon />
              </Avatar>
              <Typography variant="h4" fontWeight="bold" color="secondary.main">
                {global_activity.active_currencies_count}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('globalStats.activeCurrencies', 'Devises Actives')}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Répartition par statuts - Accordéons */}
        <Box mb={3}>
          {/* Réservations par statut */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: '#1976d2', width: 32, height: 32 }}>
                  <ReservationIcon fontSize="small" />
                </Avatar>
                <Typography variant="h6">
                  {t('globalStats.reservationBreakdown', { count: global_reservations.total_count, defaultValue: `Répartition des Réservations (${global_reservations.total_count})` })}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {global_reservations.by_status_array.map((statusItem) => (
                  <Grid item xs={6} md={4} key={statusItem.status}>
                    <Box 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="space-between"
                      p={2}
                      borderRadius={1}
                      bgcolor="grey.50"
                      border="1px solid"
                      borderColor="grey.200"
                    >
                      <Box display="flex" alignItems="center">
                        {getStatusIcon(statusItem.status)}
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {translateStatus(statusItem.status, 'reservation')}
                        </Typography>
                      </Box>
                      <Box textAlign="right">
                        <Chip 
                          label={statusItem.count}
                          size="small"
                          color={getStatusColor(statusItem.status)}
                          variant="outlined"
                        />
                        <Typography variant="caption" display="block" color="text.secondary">
                          {calculatePercentage(statusItem.count, global_reservations.total_count)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Transactions par statut */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: '#388e3c', width: 32, height: 32 }}>
                  <TransactionIcon fontSize="small" />
                </Avatar>
                <Typography variant="h6">
                  {t('globalStats.transactionBreakdown', { count: global_transactions.total_count, defaultValue: `Répartition des Transactions (${global_transactions.total_count})` })}
                </Typography>
                {global_transactions.largest_transaction.amount > 0 && (
                  <Chip 
                    icon={<TrendingUpIcon />}
                    label={`Max: ${formatGlobalAmount(global_transactions.largest_transaction.amount, global_transactions.largest_transaction.currency)}`}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {global_transactions.by_status_array.map((statusItem) => (
                  <Grid item xs={6} md={4} key={statusItem.status}>
                    <Box 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="space-between"
                      p={2}
                      borderRadius={1}
                      bgcolor="grey.50"
                      border="1px solid"
                      borderColor="grey.200"
                    >
                      <Box display="flex" alignItems="center">
                        {getStatusIcon(statusItem.status)}
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {translateStatus(statusItem.status)}
                        </Typography>
                      </Box>
                      <Box textAlign="right">
                        <Chip 
                          label={statusItem.count}
                          size="small"
                          color={getStatusColor(statusItem.status)}
                          variant="outlined"
                        />
                        <Typography variant="caption" display="block" color="text.secondary">
                          {calculatePercentage(statusItem.count, global_transactions.total_count)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Dépôts par statut */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: '#f57c00', width: 32, height: 32 }}>
                  <DepositIcon fontSize="small" />
                </Avatar>
                <Typography variant="h6">
                  {t('globalStats.depositBreakdown', { count: global_deposits.total_count, defaultValue: `Répartition des Dépôts (${global_deposits.total_count})` })}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {global_deposits.by_status_array.map((statusItem) => (
                  <Grid item xs={6} md={4} key={statusItem.status}>
                    <Box 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="space-between"
                      p={2}
                      borderRadius={1}
                      bgcolor="grey.50"
                      border="1px solid"
                      borderColor="grey.200"
                    >
                      <Box display="flex" alignItems="center">
                        {getStatusIcon(statusItem.status)}
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {translateStatus(statusItem.status)}
                        </Typography>
                      </Box>
                      <Box textAlign="right">
                        <Chip 
                          label={statusItem.count}
                          size="small"
                          color={getStatusColor(statusItem.status)}
                          variant="outlined"
                        />
                        <Typography variant="caption" display="block" color="text.secondary">
                          {calculatePercentage(statusItem.count, global_deposits.total_count)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Détail par devise */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: '#9c27b0', width: 32, height: 32 }}>
                <StatsIcon fontSize="small" />
              </Avatar>
              <Typography variant="h6">
                  {t('globalStats.currencyDetails', { count: currencies_included.length, defaultValue: `Détail par Devise (${currencies_included.length})` })}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {global_reservations.by_currency.map((currencyData, index) => {
                const transactionData = global_transactions.by_currency[index] || {};
                const depositData = global_deposits.by_currency[index] || {};
                
                return (
                  <Grid item xs={12} md={6} key={currencyData.currency}>
                    <Paper elevation={1} sx={{ p: 2 }}>
                      <Box display="flex" alignItems="center" mb={2}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 28, height: 28, mr: 1, fontSize: '0.8rem' }}>
                          {currencyData.currency}
                        </Avatar>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {currencyData.currency}
                        </Typography>
                      </Box>
                      
                      <Grid container spacing={1}>
                        <Grid item xs={4}>
                          <Box textAlign="center" p={1} bgcolor="primary.50" borderRadius={1}>
                            <Typography variant="h6" color="primary">
                              {currencyData.count}
                            </Typography>
                            <Typography variant="caption">
                              {t('globalStats.reservations', 'Réservations')}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={4}>
                          <Box textAlign="center" p={1} bgcolor="success.50" borderRadius={1}>
                            <Typography variant="h6" color="success.main">
                              {transactionData.count || 0}
                            </Typography>
                            <Typography variant="caption">
                              {t('globalStats.transactions', 'Transactions')}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={4}>
                          <Box textAlign="center" p={1} bgcolor="warning.50" borderRadius={1}>
                            <Typography variant="h6" color="warning.main">
                              {depositData.count || 0}
                            </Typography>
                            <Typography variant="caption">
                              {t('globalStats.deposits', 'Dépôts')}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                      
                      {depositData.total_amount > 0 && (
                        <Box mt={1} p={1} bgcolor="grey.50" borderRadius={1}>
                          <Typography variant="caption" color="text.secondary">
                            {t('globalStats.totalDepositsAmount', { amount: formatGlobalAmount(depositData.total_amount, currencyData.currency), defaultValue: `Montant total dépôts: ${formatGlobalAmount(depositData.total_amount, currencyData.currency)}` })}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Période d'activité globale */}
        {global_activity.has_activity && (
          <Box mt={3}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar sx={{ bgcolor: '#9c27b0', width: 32, height: 32, mr: 1 }}>
                  <CalendarIcon fontSize="small" />
                </Avatar>
                <Typography variant="h6">
                  {t('globalStats.activityPeriod', 'Période d\'Activité Globale')}
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box 
                    display="flex" 
                    alignItems="center" 
                    p={1}
                    borderRadius={1}
                    bgcolor="grey.50"
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                      {t('globalStats.firstActivity', 'Première activité :')}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatDateUniversal(global_activity.first_transaction)}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box 
                    display="flex" 
                    alignItems="center" 
                    p={1}
                    borderRadius={1}
                    bgcolor="grey.50"
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                      {t('globalStats.lastActivity', 'Dernière activité :')}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatDateUniversal(global_activity.last_transaction)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        )}

        {/* Erreurs de devises */}
        {currencies_with_errors.length > 0 && (
          <Box mt={2}>
            <Typography variant="subtitle2" color="error" gutterBottom>
              {t('globalStats.errorsLabel', { count: currencies_with_errors.length, defaultValue: `⚠️ Erreurs (${currencies_with_errors.length}):` })}
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {currencies_with_errors.map((errorItem) => (
                <Tooltip key={errorItem.currency} title={errorItem.error}>
                  <Chip 
                    label={errorItem.currency}
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                </Tooltip>
              ))}
            </Box>
          </Box>
        )}

        {/* Pied de page */}
        <Divider sx={{ mt: 3, mb: 2 }} />
        <Box 
          display="flex" 
          alignItems="center" 
          justifyContent="space-between"
          color="text.secondary"
        >
          <Typography variant="caption">
            {t('globalStats.aggregatedData', { success: metadata.successful_currencies, total: metadata.successful_currencies + metadata.failed_currencies, defaultValue: `Données agrégées: ${metadata.successful_currencies}/${metadata.successful_currencies + metadata.failed_currencies} devises` })}
          </Typography>
          <Typography variant="caption">
            {t('globalStats.generatedAt', { date: formatDateUniversal(metadata.generated_at), defaultValue: `Généré le: ${formatDateUniversal(metadata.generated_at)}` })}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default OwnerGlobalCurrencyStatsView;